import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { FaArrowUp, FaArrowDown, FaCalculator, FaWallet, FaBitcoin } from 'react-icons/fa';
import { CiBank } from 'react-icons/ci';
import { GiMoneyStack } from 'react-icons/gi';

const FinancialOverview = ({ transactions, subscriptions, bankAccounts, debts = [], dateRange }) => {
  const getAccountIcon = (accountName) => {
    const name = accountName.toLowerCase();
    if (name === 'cash') {
      return <FaWallet className="me-2" />;
    } else if (name === 'binance') {
      return <FaBitcoin className="me-2" />;
    } else if (name.includes('bank')) {
      return <CiBank className="me-2" />;
    } else {
      return <GiMoneyStack className="me-2" />;
    }
  };

  const calculateTotals = () => {
    const { startDate, endDate } = dateRange;
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    // Calculate transaction totals
    const filteredTransactions = transactions.filter(transaction =>
      moment(transaction.datetime).isBetween(startMoment, endMoment, null, '[]')
    );

    const incomingTransactions = filteredTransactions
      .filter(transaction => transaction.incoming)
      .reduce((total, transaction) => total + Number(transaction.amount), 0);

    const outgoingTransactions = filteredTransactions
      .filter(transaction => !transaction.incoming)
      .reduce((total, transaction) => total + Number(transaction.amount), 0);

    // Calculate subscription payments within date range
    const subscriptionTotals = subscriptions.reduce((totals, sub) => {
      const nextDueDate = moment(sub.next_due_date);
      
      // Only count subscriptions that are due within the selected date range
      if (nextDueDate.isBetween(startMoment, endMoment, 'day', '[]')) {
        const amount = Number(sub.amount);
        // Check if the subscription type contains 'Incoming'
        if (sub.subscription_type && sub.subscription_type.toLowerCase().includes('incoming')) {
          totals.incoming += amount; // Add to income
        } else {
          totals.outgoing += amount; // Add to expenses
        }
      }

      return totals;
    }, { incoming: 0, outgoing: 0 });

    // Calculate total expenses (outgoing transactions + outgoing subscriptions)
    const totalExpenses = outgoingTransactions + subscriptionTotals.outgoing;

    // Calculate total income (incoming transactions + incoming subscriptions)
    const totalIncome = incomingTransactions + subscriptionTotals.incoming;

    // Calculate net income
    const netIncome = totalIncome - totalExpenses;

    return {
      incomingTransactions,
      outgoingTransactions,
      subscriptionIncoming: subscriptionTotals.incoming,
      subscriptionOutgoing: subscriptionTotals.outgoing,
      totalExpenses,
      totalIncome,
      netIncome
    };
  };

  const {
    incomingTransactions,
    outgoingTransactions,
    subscriptionIncoming,
    subscriptionOutgoing,
    totalExpenses,
    totalIncome,
    netIncome
  } = calculateTotals();

  const openDebts = debts.filter((d) => d.status === 'open' || d.status === 'partial');
  const owedToMe = openDebts
    .filter((d) => d.direction === 'receivable')
    .reduce((sum, d) => sum + Number(d.remaining), 0);
  const iOwe = openDebts
    .filter((d) => d.direction === 'payable')
    .reduce((sum, d) => sum + Number(d.remaining), 0);

  return (
    <div>
      <Row className="mb-4">
        
        <Col md={4}>
          <Card className="bg-dark text-light h-100 border-secondary">
            <Card.Body>
              <Card.Title className="text-light mb-3" style={{ 
                fontWeight: 'bold',
                color: '#60A5FA'
              }}>
                <FaArrowUp className="me-2 text-success" />
                Total Income
              </Card.Title>
              <h3 className="text-success">€{totalIncome.toFixed(2)}</h3>
              <div className="text-light small">
                <div>From Transactions: €{incomingTransactions.toFixed(2)}</div>
                <div>From Subscriptions: €{subscriptionIncoming.toFixed(2)}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="bg-dark text-light h-100 border-secondary">
            <Card.Body>
              <Card.Title className="text-light mb-3" style={{ 
                fontWeight: 'bold',
                color: '#60A5FA'
              }}>
                <FaArrowDown className="me-2 text-danger" />
                Total Expenses
              </Card.Title>
              <h3 className="text-danger">€{totalExpenses.toFixed(2)}</h3>
              <div className="text-light small">
                <div>From Transactions: €{outgoingTransactions.toFixed(2)}</div>
                <div>From Subscriptions: €{subscriptionOutgoing.toFixed(2)}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="bg-dark text-light h-100 border-secondary">
            <Card.Body>
              <Card.Title className="text-light mb-3" style={{ 
                fontWeight: 'bold',
                color: '#60A5FA'
              }}>
                <FaCalculator className="me-2 text-info" />
                Net Income
              </Card.Title>
              <h3 className={netIncome >= 0 ? "text-success" : "text-danger"}>
                €{netIncome.toFixed(2)}
              </h3>
              <small className="text-light">Within selected date range</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card className="bg-dark text-light border-secondary h-100">
            <Card.Body>
              <Card.Title style={{ color: '#60A5FA', fontWeight: 'bold' }}>Money you have</Card.Title>
              <h3 className="text-warning mb-2">
                €{bankAccounts.reduce((sum, account) => sum + Number(account.total_balance), 0).toFixed(2)}
              </h3>
              <div className="small">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="d-flex justify-content-between" style={{ color: '#d1d5db' }}>
                    <span>{getAccountIcon(account.bank)}{account.bank}</span>
                    <span>€{Number(account.total_balance).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="bg-dark text-light border-secondary h-100">
            <Card.Body>
              <Card.Title style={{ color: '#60A5FA', fontWeight: 'bold' }}>Debts / IOUs</Card.Title>
              <div className="d-flex gap-4 mb-2">
                <div>
                  <div className="small" style={{ color: '#9ca3af' }}>Owed to me</div>
                  <h4 className="text-success mb-0">€{owedToMe.toFixed(2)}</h4>
                </div>
                <div>
                  <div className="small" style={{ color: '#9ca3af' }}>I owe</div>
                  <h4 className="text-danger mb-0">€{iOwe.toFixed(2)}</h4>
                </div>
              </div>
              <small style={{ color: '#9ca3af' }}>
                {openDebts.length} open · manage in the Debts tab
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="bg-dark text-light border-secondary">
        <Card.Header className="bg-dark text-light border-secondary">
          <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#60A5FA' }}>Account Overview</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            {bankAccounts.map((account) => (
              <Col key={account.id} md={4} className="mb-3">
                <Card className="bg-secondary text-light h-100 border-0">
                  <Card.Body>
                    <Card.Title>
                      <span className="text-info">{getAccountIcon(account.bank)}</span>
                      {account.bank}
                    </Card.Title>
                    <hr/>
                    <p className="mb-1 text-light">Balance: €{Number(account.total_balance).toFixed(2)}</p>
                    {account.iban && <p className="mb-1 text-light">IBAN: {account.iban}</p>}
                    {account.masked_card && account.masked_card !== 'No card linked' && (
                      <p className="mb-0 text-light">Card: {account.masked_card}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinancialOverview; 